import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '../../api/client'
import { notify } from '../../lib/notifications'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

const OCR_CODE = 'ai.ocr.provider'

type Writer = {
    POST: (
        path: string,
        opts: { params: { path: { orgCode: string; code: string } }; body: { value: unknown } }
    ) => Promise<{ error?: unknown }>
}

/**
 * The engine that reads the organization's documents (spec 2026-10-19-02):
 * Document AI, or the server's own text layer reader and Tesseract. Platform
 * admins change it, everyone else reads it. Existing documents are not read
 * again.
 */
export function OcrProviderSection({
    org,
    provider,
    options,
    canWrite,
    queryKey,
}: {
    org: string
    provider: string
    options: string[]
    canWrite: boolean
    queryKey: unknown[]
}) {
    const { t } = useTranslation('settings')
    const queryClient = useQueryClient()

    const mutation = useMutation({
        mutationFn: async (value: string) => {
            const res = await (apiClient as unknown as Writer).POST('/api/v1/organizations/{orgCode}/settings/{code}', {
                params: { path: { orgCode: org, code: OCR_CODE } },
                body: { value },
            })
            if (res.error) throw res.error
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey })
            notify({ key: 'ai_models_saved', variant: 'default', titleKey: 'settings:ai_models.saved' })
        },
        onError: () => {
            notify({
                key: 'ai_models_error',
                variant: 'destructive',
                titleKey: 'settings:ai_models.error_title',
                descriptionKey: 'settings:ai_models.error_description',
                timeoutMs: 0,
            })
        },
    })

    return (
        <section className="bg-card rounded-lg border border-border p-6 space-y-4" data-testid="ai-models-ocr">
            <div>
                <h2 className="text-base font-semibold text-foreground">{t('ai_models.ocr.title')}</h2>
                <p className="text-muted-foreground text-sm mt-1">{t('ai_models.ocr.description')}</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-[1fr_16rem] sm:items-start">
                <div className="text-sm font-medium text-foreground">{t('ai_models.provider.label')}</div>
                {canWrite ? (
                    <Select value={provider} onValueChange={(v) => mutation.mutate(v)} disabled={mutation.isPending}>
                        <SelectTrigger data-testid="ai-models-ocr-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {options.map((o) => (
                                <SelectItem key={o} value={o} data-testid={`ai-models-ocr-option-${o}`}>
                                    {t(`ai_models.ocr.option_${o}`, { defaultValue: o })}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <div className="text-sm text-foreground" data-testid="ai-models-ocr-value">
                        {provider}
                    </div>
                )}
            </div>
            <p className="text-xs text-muted-foreground">{t('ai_models.ocr.no_reread')}</p>
        </section>
    )
}
